import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, ChevronsUpDown, Loader2, RefreshCw, Trash2 } from 'lucide-react'
import type {ScmInstallation, ScmRepoBinding, ScmRepository} from '@/lib/api/features/scm';
import {
  BRANCH_FORMAT_PRESETS,
  DEFAULT_BASE_BRANCH,
  DEFAULT_BRANCH_FORMAT,
  
  
  
  createScmBinding,
  deleteScmBinding,
  listScmBindings,
  listScmBranches,
  listScmInstallations,
  listScmRepositories
} from '@/lib/api/features/scm'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useHasPermission } from '@/lib/hooks/useWorkspaceRole'
import { WORKSPACE_PERMISSIONS } from '@/lib/types/roles'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

type Props = {
  workspaceSlug: string
  projectId: string
}

/**
 * Project-level GitHub repo binding: bind one of the workspace's connected-installation
 * repositories to this project so PRs/CI/review surface on its tasks, and set the branch format
 * and linkback behaviour. The GitHub App connection itself is workspace-level and lives in
 * Workspace settings → Integrations (WorkspaceIntegrationsSection); this section shows a hint
 * when no account is connected yet. No secrets or installation ids are trusted from the client.
 */
export function GitHubConnectSection({ workspaceSlug, projectId }: Props) {
  const { t } = useTranslation('projects')
  const canManage = useHasPermission(WORKSPACE_PERMISSIONS.MANAGE_WORKSPACE_SETTINGS)

  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [installations, setInstallations] = useState<Array<ScmInstallation>>([])
  const [bindings, setBindings] = useState<Array<ScmRepoBinding>>([])
  const [repos, setRepos] = useState<Array<ScmRepository>>([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [branchPickerOpen, setBranchPickerOpen] = useState(false)
  const [refreshingRepos, setRefreshingRepos] = useState(false)
  const [refreshingBranches, setRefreshingBranches] = useState(false)
  // The bound repo's branches + its default, loaded for the base-branch picker.
  const [branches, setBranches] = useState<Array<string>>([])
  const [repoDefaultBranch, setRepoDefaultBranch] = useState<string | null>(null)
  // Which installation the repo picker is scoped to (user-switchable when >1 account is connected).
  const [selectedInstallationId, setSelectedInstallationId] = useState<string | null>(null)

  const selectedInstallation =
    installations.find((i) => i.installationId === selectedInstallationId) ??
    installations.find((i) => i.isActive) ??
    installations[0]
  const projectBinding = bindings.find((b) => b.projectId === projectId)

  // The effective base branch when the user hasn't picked one: prefer `develop`, then `main` if
  // either exists on the repo, else the repo's real default, else the server fallback. Fetching
  // real branches means the field can never point at a branch that doesn't exist.
  const preferredBaseBranch =
    (branches.includes(DEFAULT_BASE_BRANCH) && DEFAULT_BASE_BRANCH) ||
    (branches.includes('main') && 'main') ||
    repoDefaultBranch ||
    DEFAULT_BASE_BRANCH
  const effectiveBaseBranch = projectBinding?.baseBranch ?? preferredBaseBranch

  const selectBaseBranch = (name: string) => {
    setBranchPickerOpen(false)
    if (name === (projectBinding?.baseBranch ?? '')) return // no-op: unchanged
    void updateBinding({ baseBranch: name })
  }

  const load = async () => {
    const [inst, binds] = await Promise.all([
      listScmInstallations(workspaceSlug),
      listScmBindings(workspaceSlug),
    ])
    setInstallations(inst)
    setBindings(binds)
    return { inst, binds }
  }

  // Load installations + bindings. (The connect handshake and its ?scm_connected callback are
  // handled at the workspace level in WorkspaceIntegrationsSection, not here.)
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void (async () => {
      try {
        const { inst, binds } = await load()
        if (cancelled) return
        // Default the picker scope to the binding's installation if any, else active/first.
        const bound = binds.find((b) => b.projectId === projectId)
        const initial = bound?.installationId ?? (inst.find((i) => i.isActive) ?? inst[0])?.installationId ?? null
        if (!cancelled) setSelectedInstallationId(initial)
      } catch (err) {
        logger.logError(err as Error, LogCategory.API_ERROR, { context: 'scm_connect_load' })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceSlug, projectId])

  // Fetch the repo list for the selected installation. GitHub is the source of truth, so this is
  // re-runnable: on installation change (effect) and on the manual refresh button.
  const loadRepos = useCallback(async () => {
    if (!selectedInstallation) return
    const r = await listScmRepositories(workspaceSlug, selectedInstallation.installationId).catch(() => [])
    setRepos(r)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceSlug, selectedInstallation?.installationId])

  // Fetch the bound repo's branches (+ its default) for the base-branch picker. Re-runnable: on the
  // bound repo changing (effect), when the picker opens, and on the manual refresh button — so a
  // branch created on GitHub after this page loaded shows up without a full reload.
  const loadBranches = useCallback(async () => {
    if (!projectBinding) {
      setBranches([])
      setRepoDefaultBranch(null)
      return
    }
    const data = await listScmBranches(
      workspaceSlug,
      projectBinding.installationId,
      projectBinding.repositoryFullName,
    ).catch(() => ({ branches: [] as Array<string>, defaultBranch: null }))
    setBranches(data.branches)
    setRepoDefaultBranch(data.defaultBranch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceSlug, projectBinding?.installationId, projectBinding?.repositoryFullName])

  useEffect(() => {
    void loadRepos()
  }, [loadRepos])

  useEffect(() => {
    void loadBranches()
  }, [loadBranches])

  const refreshRepos = async () => {
    setRefreshingRepos(true)
    try {
      await loadRepos()
    } finally {
      setRefreshingRepos(false)
    }
  }

  const refreshBranches = async () => {
    setRefreshingBranches(true)
    try {
      await loadBranches()
    } finally {
      setRefreshingBranches(false)
    }
  }

  const bindRepo = async (repositoryFullName: string) => {
    if (!selectedInstallation) return
    setPickerOpen(false)
    setBusy(true)
    try {
      await createScmBinding(workspaceSlug, {
        repositoryFullName,
        projectId,
        installationId: selectedInstallation.installationId,
      })
      await load()
      toast.success(t('github.linkedRepo'))
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message ?? t('github.connectError'))
    } finally {
      setBusy(false)
    }
  }

  const updateBinding = async (patch: {
    branchFormat?: string
    baseBranch?: string
    linkbacksEnabled?: boolean
  }) => {
    if (!projectBinding) return
    setBusy(true)
    try {
      await createScmBinding(workspaceSlug, {
        repositoryFullName: projectBinding.repositoryFullName,
        projectId,
        // Keep the binding on ITS OWN installation — not whichever is currently "active" (with
        // multiple connected accounts activeInstallation may differ from the binding's owner).
        installationId: projectBinding.installationId,
        ...patch,
      })
      await load()
      if (patch.branchFormat !== undefined) toast.success(t('github.branchFormatSaved'))
      if (patch.baseBranch !== undefined) toast.success(t('github.baseBranchSaved'))
    } catch {
      toast.error(t('github.connectError'))
    } finally {
      setBusy(false)
    }
  }

  const unbind = async (bindingId: string) => {
    setBusy(true)
    try {
      await deleteScmBinding(workspaceSlug, bindingId)
      await load()
      toast.success(t('github.unlinkedRepo'))
    } catch (err) {
      logger.logError(err as Error, LogCategory.API_ERROR, { context: 'scm_unbind' })
      toast.error(t('github.connectError'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Separator className="my-8" />
      <div>
        <p className="text-sm font-medium mb-1">{t('github.title')}</p>
        <p className="text-xs text-muted-foreground mb-4">{t('github.description')}</p>

        <div className="rounded-lg border bg-card p-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {t('github.title')}…
            </div>
          ) : !selectedInstallation ? (
            <p className="text-sm text-muted-foreground">{t('github.needsWorkspaceConnect')}</p>
          ) : (
            <div className="space-y-4">
              {installations.length > 1 && (
                // Multiple connected accounts — let the user pick which one to bind a repo from.
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="inline-block size-2 rounded-full bg-green-500" />
                  <Select
                    value={selectedInstallation.installationId}
                    onValueChange={setSelectedInstallationId}
                    disabled={busy}
                  >
                    <SelectTrigger className="h-6 w-48 text-base md:text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {installations.map((i) => (
                        <SelectItem key={i.installationId} value={i.installationId} className="text-xs">
                          {(i.accountLogin ?? i.installationId) + (i.isActive ? '' : ' (suspended)')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-xs">{t('github.repository')}</Label>
                {projectBinding ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-mono">{projectBinding.repositoryFullName}</span>
                      <span>{t('github.linked')}</span>
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-6"
                          onClick={() => unbind(projectBinding.id)}
                          disabled={busy}
                          title={t('github.unlink')}
                        >
                          <Trash2 className="size-3" />
                        </Button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground">{t('github.branchFormat')}</Label>
                      <Select
                        value={projectBinding.branchFormat ?? DEFAULT_BRANCH_FORMAT}
                        onValueChange={(v) => void updateBinding({ branchFormat: v })}
                        disabled={!canManage || busy}
                      >
                        <SelectTrigger className="h-7 w-64 font-mono text-base md:text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {BRANCH_FORMAT_PRESETS.map((f) => (
                            <SelectItem key={f} value={f} className="font-mono text-xs">
                              {f}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground">{t('github.baseBranch')}</Label>
                      <Popover
                        open={branchPickerOpen}
                        onOpenChange={(open) => {
                          setBranchPickerOpen(open)
                          if (open) void refreshBranches() // pull any branches added on GitHub since load
                        }}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            role="combobox"
                            className="h-7 w-64 justify-between font-mono text-xs"
                            disabled={!canManage || busy}
                          >
                            {effectiveBaseBranch}
                            <ChevronsUpDown className="size-3.5 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-0" align="start">
                          <Command>
                            <CommandInput placeholder={t('github.searchBranches')} className="text-xs" />
                            <CommandList>
                              <CommandEmpty>{t('github.noBranches')}</CommandEmpty>
                              <CommandGroup>
                                {branches.map((b) => (
                                  <CommandItem
                                    key={b}
                                    value={b}
                                    onSelect={() => selectBaseBranch(b)}
                                    className="font-mono text-xs"
                                  >
                                    <Check className={cn('size-3.5', effectiveBaseBranch === b ? 'opacity-100' : 'opacity-0')} />
                                    {b}
                                    {repoDefaultBranch === b && (
                                      <span className="ml-auto text-[10px] text-muted-foreground">
                                        {t('github.defaultBranchTag')}
                                      </span>
                                    )}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        onClick={() => void refreshBranches()}
                        disabled={!canManage || busy || refreshingBranches}
                        title={t('github.refreshBranches')}
                      >
                        <RefreshCw className={cn('size-3.5', refreshingBranches && 'animate-spin')} />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-muted-foreground">{t('github.linkbacks')}</Label>
                      <Switch
                        checked={projectBinding.linkbacksEnabled}
                        onCheckedChange={(v) => void updateBinding({ linkbacksEnabled: v })}
                        disabled={!canManage || busy}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                  <Popover
                    open={pickerOpen}
                    onOpenChange={(open) => {
                      setPickerOpen(open)
                      if (open) void refreshRepos() // pull any repos newly granted to the installation
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        role="combobox"
                        className="w-72 justify-between text-xs"
                        disabled={!canManage || busy}
                      >
                        {t('github.selectRepo')}
                        <ChevronsUpDown className="size-3.5 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-0" align="start">
                      <Command>
                        <CommandInput placeholder={t('github.searchRepos')} className="text-xs" />
                        <CommandList>
                          <CommandEmpty>{t('github.noRepos')}</CommandEmpty>
                          <CommandGroup>
                            {repos.map((r) => (
                              <CommandItem
                                key={r.fullName}
                                value={r.fullName}
                                onSelect={() => bindRepo(r.fullName)}
                                className="text-xs"
                              >
                                <Check className={cn('size-3.5', 'opacity-0')} />
                                {r.fullName}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={() => void refreshRepos()}
                    disabled={!canManage || busy || refreshingRepos}
                    title={t('github.refreshRepos')}
                  >
                    <RefreshCw className={cn('size-3.5', refreshingRepos && 'animate-spin')} />
                  </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
